import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Col, Container, FormSelect, InputGroup, Row} from "react-bootstrap";
import {MaybeDate} from "../../types/Maybe";
import {
    dateTimeToString,
    dateToString,
    dateWithZeroTime, getDateAsPeriod, zeroToEmptyString,
} from "../../tools";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faFileExcel} from "@fortawesome/free-solid-svg-icons";
import RRTable from "../../components/rr-table/RRTable";
import {useAppSelector} from "../../hooks";
import {selectAllEmployees} from "../../../features/AppDataSlice";
import {selectDepartment} from "../../../features/AppStateSlice";
import {RREmployeeType} from "../../types/RREmployeeType";
import {utils, writeFile} from "xlsx";
import {getReportMonthOptions} from "./reportMonthOptions";
import {getEmployeeRateEffectiveFrom} from "../employees/employeeRateAudit";

type ReportItemType = {
    emplName: string;
    changeDate: Date;
    date: string;
    oldBaseRate: number;
    oldSupervisorRate: number;
    newBaseRate: number;
    newSupervisorRate: number;
    approved: boolean;
    createdAt: string;
    createdBy: string;
    approvedAt: string;
    approvedBy: string;
}

const ReportPayRateChangeView: FC = () => {
    const currentDepartment = useAppSelector(selectDepartment);
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const working = false;
    const [periodFrom, setPeriodFrom] = useState<Date>(new Date());
    const [periodTo, setPeriodTo] = useState<Date>(new Date());
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [reportItems, setReportItems] = useState<ReportItemType[]>([]);
    const tblRef = useRef<HTMLTableElement>(null);

    const setRefPeriod = useCallback(() => {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom)!);
        const [, periodEnd] = getDateAsPeriod(dpPeriodTo!);
        setPeriodTo(dateWithZeroTime(periodEnd)!);
    }, [dpPeriodFrom, dpPeriodTo]);

    // Изборът на месец попълва началната и крайната дата с целия съответен период.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    const employees = useAppSelector(selectAllEmployees);

    const employeeMatchesCurrentDepartment = useCallback((employee: RREmployeeType): boolean => {
        if (currentDepartment === "montage") return employee.isMontage;
        if (currentDepartment === "catering") return employee.isCatering;
        if (currentDepartment === "store") return employee.isStore;
        if (currentDepartment === "logistics") return employee.isLogistics;
        return false;
    }, [currentDepartment]);

    useEffect(() => {
        setReportItems([]);
        setRefPeriod();
    }, []);

    useEffect(() => {
        const _reportItems: ReportItemType[] = [];
        employees
            .filter(employeeMatchesCurrentDepartment)
            .forEach(e => {
                e.empl_pph?.forEach((p, idx) => {
                    const effectiveFrom = getEmployeeRateEffectiveFrom(p);
                    if (idx > 0 && effectiveFrom >= periodFrom && effectiveFrom <= periodTo) {
                        _reportItems.push({
                            emplName: e.name,
                            changeDate: effectiveFrom,
                            date: dateTimeToString(effectiveFrom),
                            oldBaseRate: idx > 0 ? e.empl_pph![idx - 1].pph : 0,
                            oldSupervisorRate: idx > 0 ? e.empl_pph![idx - 1].supervisor_pph : 0,
                            newBaseRate: p.pph,
                            newSupervisorRate: p.supervisor_pph,
                            approved: p.isApproved,
                            createdAt: dateTimeToString(p.createdAt),
                            createdBy: p.createdBy?.name || "",
                            approvedAt: dateTimeToString(p.approvedAt),
                            approvedBy: p.approvedBy?.name || "",
                        });
                    }
                });
            }
        );
        setReportItems(_reportItems);
    }, [employeeMatchesCurrentDepartment, employees, periodFrom, periodTo]);


    function exportExcel(): void {
        const workbook = utils.table_to_book(tblRef.current,);
        writeFile(workbook, "Report Rate Change.xlsx");
    }


    return <Container fluid>
        <h1>Справкa за промяна на ставките за период</h1>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"periodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"periodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working && <>
                                <Button onClick={setRefPeriod} disabled={!dpPeriodFrom || !dpPeriodTo}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={"outline-success"}><FontAwesomeIcon
                                    icon={faFileExcel}/></Button>&nbsp;
                            </>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        <Row className={"g-2 mt-3"}>
            <Col>
                {
                    <RRTable ref={tblRef}>
                        <thead className={"text-center"}>
                        <tr>
                            <th rowSpan={2}>Служител</th>
                            <th rowSpan={2}>От дата</th>
                            <th colSpan={2}>Предишна</th>
                            <th colSpan={2}>Нова</th>
                            <th className={"text-center"} rowSpan={2}>Одобрена?</th>
                            <th rowSpan={2}>Създадена на</th>
                            <th rowSpan={2}>Създадена от</th>
                            <th rowSpan={2}>Одобрена на</th>
                            <th rowSpan={2}>Одобрена от</th>
                        </tr>
                        <tr>
                            <th className={"text-end"}>Базова ставка</th>
                            <th className={"text-end"}>Суперв. ставка</th>
                            <th className={"text-end"}>Базова ставка</th>
                            <th className={"text-end"}>Суперв. ставка</th>
                        </tr>
                        </thead>

                        <tbody>
                        {
                            reportItems
                                .sort((a, b) => a.changeDate.getTime() - b.changeDate.getTime())
                                .map(
                                (r, idx) => {
                                    return <tr key={idx}>
                                        <td>{r.emplName}</td>
                                        <td>{r.date.substring(0, 10)}</td>
                                        <td className={"text-end"}>{zeroToEmptyString(r.oldBaseRate)}</td>
                                        <td className={"text-end"}>{zeroToEmptyString(r.oldSupervisorRate)}</td>
                                        <td className={"text-end"}>{r.newBaseRate}</td>
                                        <td className={"text-end"}>{r.newSupervisorRate}</td>
                                        <td className={"text-center"}>{r.approved ? "Да" : "Не"}</td>
                                        <td>{r.createdAt}</td>
                                        <td>{r.createdBy}</td>
                                        <td>{r.approvedAt}</td>
                                        <td>{r.approvedBy}</td>
                                    </tr>
                                }
                            )
                        }

                        {
                            reportItems.length === 0 &&
                            <tr>
                                <td colSpan={11} className={"text-center fw-bold"}><br/>Няма данни за периода<br/>&nbsp;</td>
                            </tr>
                        }
                        </tbody>
                    </RRTable>
                }
            </Col>
        </Row>

    </Container>
}

export default ReportPayRateChangeView;
